import { useEffect, type ReactNode } from 'react'
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useMatch,
} from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryClient } from './providers.js'
import { ROUTES } from './routes.js'
import { useAuthStore, authStore } from '../shared/store/auth.store.js'
import { useOrgStore } from '../shared/store/org.store.js'
import { isPlatformAdminRole } from '../shared/utils/permissions.js'
import AppLayout from './AppLayout.js'
import LoginPage from '../pages/auth/LoginPage.js'
import RegisterPage from '../pages/auth/RegisterPage.js'
import DashboardPage from '../pages/dashboard/DashboardPage.js'
import MenuPage from '../pages/menu/MenuPage.js'
import MenuDetailPage from '../pages/menu/MenuDetailPage.js'
import LocationsPage from '../pages/locations/LocationsPage.js'
import CitiesPage from '../pages/platform/CitiesPage.js'
import PlatformHomePage from '../pages/platform/PlatformHomePage.js'
import OrganizationsPage from '../pages/platform/OrganizationsPage.js'
import OrganizationDetailPage from '../pages/platform/OrganizationDetailPage.js'
import PlatformAdministratorsPage from '../pages/platform/PlatformAdministratorsPage.js'
import LocationDetailPage from '../pages/locations/LocationDetailPage.js'
import LocationForm from '../pages/locations/components/LocationForm.js'
import ProductsPage from '../pages/products/ProductsPage.js'
import OrdersPage from '../pages/orders/OrdersPage.js'
import OrderDetailPage from '../pages/orders/OrderDetailPage.js'
import CustomersPage from '../pages/customers/CustomersPage.js'
import CustomerDetailPage from '../pages/customers/CustomerDetailPage.js'
import SettingsPage from '../pages/settings/SettingsPage.js'
import OnboardingPage from '../pages/onboarding/OnboardingPage.js'
import LoadingPage from '../pages/loading/LoadingPage.js'
import RootRoute from './RootRoute.js'
import PlatformGuard from './PlatformGuard.js'
import PlatformLayout from './PlatformLayout.js'
import StorefrontPage from '../pages/storefront/StorefrontPage.js'
import OrderTrackingPage from '../pages/order-tracking/OrderTrackingPage.js'
import DeliveryZonesPage from '../pages/delivery-zones/DeliveryZonesPage.js'
import '../shared/i18n/setup.js'

authStore.bootstrap()

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path={ROUTES.HOME} element={<RootRoute />} />
          <Route
            path={ROUTES.LOGIN}
            element={
              <GuestGuard>
                <LoginPage />
              </GuestGuard>
            }
          />
          <Route
            path={ROUTES.REGISTER}
            element={
              <GuestGuard>
                <RegisterPage />
              </GuestGuard>
            }
          />
          <Route path={ROUTES.DASHBOARD} element={<AppGuard />}>
            <Route element={<AppLayout />}>
              <Route index element={<Navigate to={ROUTES.ORDERS} replace />} />
              <Route path="overview" element={<DashboardPage />} />
              <Route path="restaurant" element={<Navigate to={ROUTES.SETTINGS} replace />} />
              <Route path="menu" element={<MenuPage />} />
              <Route path="menu/:menuId" element={<MenuDetailPage />} />
              <Route path="locations" element={<LocationsPage />} />
              <Route path="locations/new" element={<LocationFormWrapper />} />
              <Route path="locations/:locationId/edit" element={<LocationFormWrapper />} />
              <Route path="locations/:locationId" element={<LocationDetailPage />} />
              <Route path="locations/:locationId/delivery-zones" element={<DeliveryZonesPage />} />
              <Route path="products" element={<ProductsPage />} />
              <Route path="orders" element={<OrdersPage />} />
              <Route path="orders/:orderId" element={<OrderDetailPage />} />
              <Route path="customers" element={<CustomersPage />} />
              <Route path="customers/:customerKey" element={<CustomerDetailPage />} />
              <Route path="settings" element={<SettingsPage />} />
            </Route>
            <Route path="onboarding" element={<OnboardingPage />} />
          </Route>
          <Route path={ROUTES.CITIES} element={<Navigate to={ROUTES.PLATFORM_CITIES} replace />} />
          <Route path={ROUTES.PLATFORM} element={<PlatformGuard />}>
            <Route element={<PlatformLayout />}>
              <Route index element={<PlatformHomePage />} />
              <Route path="organizations" element={<OrganizationsPage />} />
              <Route path="organizations/:organizationId" element={<OrganizationDetailPage />} />
              <Route path="cities" element={<CitiesPage />} />
              <Route path="administrators" element={<PlatformAdministratorsPage />} />
            </Route>
          </Route>
          <Route path={ROUTES.STOREFRONT_ORG} element={<StorefrontPage />} />
          <Route path={ROUTES.STOREFRONT_LOCATION} element={<StorefrontPage />} />
          <Route path={ROUTES.STOREFRONT_ORDER} element={<OrderTrackingPage />} />
          <Route path="*" element={<Navigate to={ROUTES.HOME} replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  )
}

function GuestGuard({ children }: { children: ReactNode }) {
  const status = useAuthStore(s => s.status)
  if (status === 'initializing') return <LoadingPage />
  if (status === 'authenticated') return <Navigate to={ROUTES.ORDERS} replace />
  return <>{children}</>
}

function AppGuard() {
  const status = useAuthStore(s => s.status)
  if (status === 'initializing') return <LoadingPage />
  if (status !== 'authenticated') return <Navigate to={ROUTES.LOGIN} replace />
  return <OrganizationGate />
}

function OrganizationGate() {
  const status = useOrgStore(s => s.status)
  const load = useOrgStore(s => s.load)
  const platformRole = useAuthStore(s => s.user?.platformRole)
  const onOnboarding = useMatch(ROUTES.ONBOARDING) !== null

  useEffect(() => {
    if (status === 'idle') void load()
  }, [status, load])

  if (status === 'idle' || status === 'loading') return <LoadingPage />
  if (status === 'error') return <OrgLoadError onRetry={() => void load()} />

  if (status === 'empty') {
    // Platform staff do not need an organization membership. The platform is a
    // separate workspace, so never push them into restaurant onboarding.
    if (isPlatformAdminRole(platformRole)) {
      return <Navigate to={ROUTES.PLATFORM} replace />
    }
    return onOnboarding ? <Outlet /> : <Navigate to={ROUTES.ONBOARDING} replace />
  }

  // status === 'ready'
  return onOnboarding ? <Navigate to={ROUTES.ORDERS} replace /> : <Outlet />
}

function OrgLoadError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="app-layout">
      <main className="app-main" style={{ textAlign: 'center', padding: '4rem' }}>
        <p>{t('error.generic')}</p>
        <button type="button" className="btn btn-primary" onClick={onRetry}>
          {t('error.retry')}
        </button>
      </main>
    </div>
  )
}

function LocationFormWrapper() {
  const match = useMatch('/app/locations/:locationId/edit')
  const locationId = match?.params?.locationId ?? null
  const orgId = useOrgStore(s => s.activeOrganization)?.id
  if (!orgId) return null
  return <LocationForm organizationId={orgId} locationId={locationId} />
}
